import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useSalesAuth } from "@/lib/auth";
import { LayoutDashboard, Building2, LogOut, MessageCircle, Ticket, UserRound, Puzzle, Layers, Wallet, Megaphone, Users, Phone, Receipt, Palette, FileText, ShieldAlert, Bell, ShoppingCart, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import Brand from "@/components/Brand";
import { PLATFORM_LOGO_URL } from "@/lib/brand";
import DashboardShell from "@/components/DashboardShell";
import SalesLiveAlerts from "./SalesLiveAlerts";

const NAV = [
  { to: "/sales", icon: LayoutDashboard, label: "Overview", testId: "sales-nav-dashboard", end: true },
  { to: "/sales/crm", icon: Users, label: "CRM", testId: "sales-nav-crm" },
  { to: "/sales/whatsapp", icon: Phone, label: "WhatsApp", testId: "sales-nav-whatsapp" },
  { to: "/sales/invoices", icon: FileText, label: "Invoice", testId: "sales-nav-invoice" },
  { to: "/sales/tenants", icon: Building2, label: "Tenant", testId: "sales-nav-tenants" },
  { to: "/sales/theme-settings", icon: Palette, label: "Theme Settings", testId: "sales-nav-theme-settings" },
  { to: "/sales/adult-control", icon: ShieldAlert, label: "Adult Control", testId: "sales-nav-adult-control" },
  { to: "/sales/purchase-billing", icon: Receipt, label: "Purchase & billing", testId: "sales-nav-purchase-billing" },
  { to: "/sales/abandoned-cart", icon: ShoppingCart, label: "Abandoned Cart", testId: "sales-nav-abandoned-cart" },
  { to: "/sales/app-live", icon: Smartphone, label: "App Live Request", testId: "sales-nav-app-live" },
  { to: "/sales/addons", icon: Puzzle, label: "Paid Addon", testId: "sales-nav-addons" },
  { to: "/sales/bulk-addons", icon: Layers, label: "Bulk Addons", testId: "sales-nav-bulk-addons" },
  { to: "/sales/wallet", icon: Wallet, label: "Wallet & Referral", testId: "sales-nav-wallet" },
  { to: "/sales/notices", icon: Megaphone, label: "Merchant popups", testId: "sales-nav-notices" },
  { to: "/sales/push", icon: Bell, label: "App push", testId: "sales-nav-push" },
  { to: "/sales/support", icon: MessageCircle, label: "Support", testId: "sales-nav-support", badgeKey: "support" },
  { to: "/sales/coupons", icon: Ticket, label: "Signup Coupons", testId: "sales-nav-coupons" },
  { to: "/sales/leads", icon: UserRound, label: "Chatbot Leads", testId: "sales-nav-leads" },
];

export default function SalesLayout() {
  const { user, logout } = useSalesAuth();
  const nav = useNavigate();
  const [supportUnread, setSupportUnread] = useState(0);
  useEffect(() => {
    let alive = true;
    const tick = () => api.get("/platform/support/unread_count").then(({ data }) => { if (alive) setSupportUnread(data.unread || 0); }).catch(() => {});
    tick();
    const id = setInterval(tick, 10000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const Sidebar = ({ onNav }) => (
    <div className="w-full bg-[#090D16] text-slate-100 flex flex-col h-full">
      <div className="p-5 border-b border-slate-800/80">
        <div data-testid="sales-brand-logo" className="min-w-0 w-full max-w-full">
          <Brand variant="sidebar" size="sm" src={PLATFORM_LOGO_URL} className="max-w-full" />
        </div>
        <div className="mt-3">
          <div className="font-display font-extrabold text-sm">Sales panel</div>
          <div className="text-[10px] text-slate-500 uppercase tracking-wider">Client management</div>
        </div>
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto dash-scroll-dark">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testId} onClick={onNav} className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-lg text-sm ${isActive ? "bg-teal-500/15 text-teal-200 border border-teal-400/20" : "text-slate-400 hover:text-white hover:bg-white/5"}`}>
            <n.icon className="w-4 h-4" />
            <span className="flex-1">{n.label}</span>
            {n.badgeKey === "support" && supportUnread > 0 && (
              <span className="min-w-[20px] h-5 rounded-full bg-orange-500 text-slate-900 text-[11px] font-bold flex items-center justify-center px-1.5" data-testid="sales-nav-support-badge">{supportUnread}</span>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-slate-800/80">
        <div className="px-3 py-2 text-xs text-slate-500 truncate" data-testid="sales-user-label">{user?.username || user?.name}</div>
        <button onClick={async () => { await logout(); toast.success("Signed out"); nav("/sales/login"); }} data-testid="sales-logout" className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-400 hover:text-white hover:bg-white/5"><LogOut className="w-4 h-4" /> Sign out</button>
      </div>
    </div>
  );

  return (
    <DashboardShell
      menuTestId="sales-mobile-menu"
      shellClassName="bg-[#090D16] text-slate-100"
      asideClassName="bg-[#090D16] border-r border-slate-800/80"
      asideWidthClass="w-60"
      mobileBarClassName="bg-[#090D16] text-slate-100 border-slate-800/80"
      menuButtonClassName="text-slate-100 hover:bg-white/10"
      sheetClassName="p-0 w-64 max-w-[85vw] bg-[#090D16] border-slate-800"
      renderSidebar={(onNav) => <Sidebar onNav={onNav} />}
      brand={(
        <div className="min-w-0 max-w-[150px]" data-testid="sales-brand-logo-mobile">
          <Brand variant="dark" size="sm" src={PLATFORM_LOGO_URL} className="max-h-8 max-w-full overflow-hidden" />
        </div>
      )}
    >
      <SalesLiveAlerts />
      <Outlet />
    </DashboardShell>
  );
}
